import React, { useEffect, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { roomMaintainenceTooltip } from "../../../constants/TooltipInformation";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  frequencyList,
  postTypeList,
  resortFeeList,
  typeList,
  villaHomeTemplateList,
} from "../../../constants/RoomMaintainence";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { payloadConstants } from "../../../constants/defaultValues";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";

const normalizePostTypeName = (value: unknown): string | null => {
  if (value == null || value === "") {
    return null;
  }
  const str = String(value);
  if (str.includes(" - ")) {
    return str.split(" - ").slice(1).join(" - ").trim();
  }
  return str;
};

const matchPostTypeOption = (
  saved: string | null | undefined,
  options: string[],
): string | null => {
  if (!saved) return null;
  const direct = options.find((o) => o === saved);
  if (direct) return direct;
  const byName = options.find(
    (o) =>
      normalizePostTypeName(o) === saved ||
      o.endsWith(` - ${saved}`) ||
      normalizePostTypeName(o)?.toLowerCase() === saved.toLowerCase(),
  );
  return byName ?? saved;
};

const normalizeSectionRows = (value: unknown): any[] => {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const RoomtypeMaintainence = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  // Derive post type names from the Post Type screen data in the payload
  const dynamicPostTypeList: string[] = React.useMemo(() => {
    const rows = fieldValues?.["Post Type"];
    if (!Array.isArray(rows) || rows.length === 0) return postTypeList;
    const names = rows
      .map((r: any) => r?.postTypeName)
      .filter((n: any) => typeof n === "string" && n.trim());
    return names.length > 0 ? names : postTypeList;
  }, [fieldValues]);
  const [roomTypeCode, setRoomTypeCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [roomTypeName, setRoomTypeName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);
  const [allowedReservationType, setAllowedReservationType] = useState<
    string | null
  >(null);
  const [buildingList, setBuildingList] = useState<string[]>([]);
  const [building, setBuilding] = useState<string[]>([]);
  const [roomCount, setRoomCount] = useState<number | null>(null);
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [maxNights, setMaxNights] = useState<number | null>(null);
  const [bedded, setBedded] = useState<boolean>(true);
  const [perm, setPerm] = useState<boolean>(false);
  const [block, setBlock] = useState<boolean>(false);
  const [blockingReminders, setBlockingReminders] = useState<boolean>(false);
  const [allowOversell, setAllowOversell] = useState<boolean>(false);
  const [suiteRoom, setSuiteRoom] = useState<boolean>(false);
  const [passcodeRoom, setPasscodeRoom] = useState<boolean>(false);
  const [ROH, setROH] = useState<boolean>(false);
  const [resortFee, setResortFee] = useState<number | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [resortFeeError, setResortFeeError] = useState<boolean>(false);
  const [resortFeeType, setResortFeeType] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [postType, setPostType] = useState<string | null>(null);
  const [postTypeOptions, setPostTypeOptions] = useState<string[]>([]);
  const [adult, setAdult] = useState<boolean>(false);
  const [youth, setYouth] = useState<boolean>(false);
  const [teen, setTeen] = useState<boolean>(false);
  const [child, setChild] = useState<boolean>(false);
  const [todlers, setTodlers] = useState<boolean>(false);
  const [villaHomeTemplateCode, setVillaHomeTemplateCode] = useState<
    string | null
  >("VillaTemplate1");
  const [countRoomAs, setCountRoomAs] = useState<number | null>(1);
  const [isHourly, setIsHourly] = useState<boolean>(false);
  const [isNightly, setIsNightly] = useState<boolean>(false);
  const [petAllowed, setPetAllowed] = useState<boolean>(false);
  const [petCount, setPetCount] = useState<number | null>(null);
  const [gapTime, setGapTime] = useState<number | null>(null);
  const [minOccupancy, setMinOccupancy] = useState<number | null>(null);
  const [maxOccupancy, setMaxOccupancy] = useState<number | null>(null);
  const [licenseFromDate, setLicenseFromDate] = useState<Dayjs | null>(
    dayjs(
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].LICENSE_FROM_DATE,
    ),
  );
  const [licenseToDate, setLicenseToDate] = useState<Dayjs | null>(
    dayjs(
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].LICENSE_TO_DATE,
    ),
  );
  const [roomMaintainenceInformation, setRoomMaintainenceInformation] =
    useState<any[]>(() =>
      normalizeSectionRows(
        fieldValues?.[RoomPropertyEnum.roomTypeMaintainence],
      ),
    );
  const hasSyncedToParentRef = useRef(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const roomMaintainencePayload = {
    code: roomTypeCode,
    name: roomTypeName,
    buildingCode: building.map((code) =>
      typeof code === "string" && code.includes(" - ")
        ? code.split(" - ")[0].trim()
        : code,
    ),
    noOfRooms:
      roomCount ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].NO_OF_ROOMS,
    maximumGuests:
      guestCount ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].MAX_GUESTS,
    maximumNights:
      maxNights ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].MAX_NIGHTS,
    isBedded: bedded,
    isUnblockPasscode: passcodeRoom,
    isBlockRequired: block,
    isAllowOversell: allowOversell,
    isBlockingReminder: blockingReminders,
    isSuite: suiteRoom,
    amount:
      resortFee ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].RESORT_FEE,
    resortFeeType:
      resortFeeType ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].RESORT_FEE_TYPE,
    feeFrequency:
      frequency ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].FEE_FREQUENCY,
    adult,
    youth,
    teen,
    child,
    todlers,
    isROH: ROH,
    villaHomeTemplateCode:
      villaHomeTemplateCode ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence]
        .VILLA_HOME_TEMPLATE_CODE,
    postTypeName:
      normalizePostTypeName(postType) ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].POST_TYPE_NAME,
    countRoomAs:
      countRoomAs ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].COUNT_ROOM_AS,
    isPetAllowed: petAllowed ?? false,
    noOfPets: petCount,
    isNightly,
    isHourly,
    gapTime,
    minimumOccupancy: minOccupancy,
    maximumOccupancy: maxOccupancy,
    type:
      allowedReservationType ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].TYPE,
    isPerm: perm,
    isActive: active,
    licenseFromDate:
      licenseFromDate?.format("YYYY-MM-DD") ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].LICENSE_FROM_DATE,
    licenseToDate:
      licenseToDate?.format("YYYY-MM-DD") ??
      payloadConstants[RoomPropertyEnum.roomTypeMaintainence].LICENSE_TO_DATE,
  };

  const addRoomMaintainence = () => {
    if (validateInput()) {
      if (!roomTypeCode || roomTypeCode === null || roomTypeCode === "") {
        setCodeError(true);
      }
      if (!roomTypeName || roomTypeName === null || roomTypeName === "") {
        setNameError(true);
      }
      if (!building?.length) {
        setAlertMessage("Enter building code");
        setAlertType("error");
        setOpenAlert(true);
      } else if (
        checkDuplicate(
          Array.isArray(roomMaintainenceInformation)
            ? roomMaintainenceInformation
            : [],
          DNDFieldsEnum.ROOMTYPECODE,
          roomTypeCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setRoomMaintainenceInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...roomMaintainencePayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...roomMaintainencePayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(roomMaintainencePayload);
        editRoomMaintainence(null);
        setEditClicked(false);
      }
    }
  };

  const editRoomMaintainence = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setRoomTypeCode(list?.code);
    setRoomTypeName(list?.name);
    setBuilding(
      list?.buildingCode
        ? (Array.isArray(list.buildingCode)
            ? list.buildingCode
            : [list.buildingCode]
          ).map((code: string) =>
            typeof code === "string" && code.includes(" - ")
              ? code.split(" - ")[0].trim()
              : code,
          )
        : [],
    );
    setRoomCount(list?.noOfRooms);
    setGuestCount(list?.maximumGuests);
    setMaxNights(list?.maximumNights);
    setBedded(list ? list.isBedded : true);
    setPasscodeRoom(list ? list.isUnblockPasscode : false);
    setBlock(list ? list.isBlockRequired : false);
    setAllowOversell(list ? list.isAllowOversell : false);
    setBlockingReminders(list ? list.isBlockingReminder : false);
    setSuiteRoom(list ? list.isSuite : false);
    setResortFee(list?.amount);
    setResortFeeType(list?.resortFeeType);
    setFrequency(list?.feeFrequency);
    setAdult(list ? list.adult : false);
    setYouth(list ? list.youth : false);
    setTeen(list ? list.teen : false);
    setChild(list ? list.child : false);
    setTodlers(list ? list.todlers : false);
    setROH(list ? list.isROH : false);
    setVillaHomeTemplateCode(list?.villaHomeTemplateCode);
    setPostType(matchPostTypeOption(list?.postTypeName, postTypeOptions));
    setCountRoomAs(list?.countRoomAs);
    setPetAllowed(list?.isPetAllowed);
    setPetCount(list?.noOfPets);
    setIsNightly(list ? list.isNightly : false);
    setIsHourly(list ? list.isHourly : false);
    setGapTime(list?.gapTime);
    setMinOccupancy(list?.minimumOccupancy);
    setMaxOccupancy(list?.maximumOccupancy);
    setAllowedReservationType(list?.type);
    setPerm(list?.isPerm ?? list?.perm ?? false);
    setActive(list?.isActive ?? true);
    setLicenseFromDate(
      list?.licenseFromDate
        ? dayjs(list.licenseFromDate)
        : dayjs(
            payloadConstants[RoomPropertyEnum.roomTypeMaintainence]
              .LICENSE_FROM_DATE,
          ),
    );
    setLicenseToDate(
      list?.licenseToDate
        ? dayjs(list.licenseToDate)
        : dayjs(
            payloadConstants[RoomPropertyEnum.roomTypeMaintainence]
              .LICENSE_TO_DATE,
          ),
    );
  };

  const updateRoomMaintainence = () => {
    if (!validateInput()) return;
    if (!roomTypeCode || roomTypeCode === null || roomTypeCode === "") {
      setCodeError(true);
      return;
    }
    if (!roomTypeName || roomTypeName === null || roomTypeName === "") {
      setNameError(true);
      return;
    }
    if (!building?.length) {
      setAlertMessage("Enter building code");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(roomMaintainenceInformation)
          ? roomMaintainenceInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ROOMTYPECODE,
        roomTypeCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoomMaintainenceInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...roomMaintainencePayload,
              };
            }
            return item;
          })
        : prev,
    );
    editRoomMaintainence(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RoomPropertyEnum.roomTypeMaintainence} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteRoomMaintainence = (id: number) => {
    setRoomMaintainenceInformation(deleteItem(roomMaintainenceInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    let isValid = true;
    if (
      roomTypeCode &&
      !validateString(
        roomTypeCode,
        validationLength.roomTypeDefinitionCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }
    if (
      roomTypeName &&
      !validateString(
        roomTypeName,
        validationLength.roomTypeDefinitionName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      isValid = false;
    }
    if (
      resortFee &&
      !validateIntegerLength(resortFee, validationLength.resortFee)
    ) {
      setResortFeeError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    if (!hasSyncedToParentRef.current) {
      hasSyncedToParentRef.current = true;
      return;
    }
    data(roomMaintainenceInformation);
    editClicked &&
      setSelectedId(
        checkData(roomMaintainenceInformation, selectedId) ? selectedId : null,
      );
  }, [roomMaintainenceInformation]);

  useEffect(() => {
    validateInput();
  }, [roomTypeCode, roomTypeName, resortFee]);

  useEffect(() => {
    !petAllowed && setPetCount(null);
  }, [petAllowed]);

  useEffect(() => {
    if (!isNightly) {
      setGapTime(null);
      setMinOccupancy(null);
      setMaxOccupancy(null);
    }
  }, [isNightly]);

  useEffect(() => {
    const buildingError = !building?.length;
    if (codeError || nameError || buildingError) {
      setOpenAccordion(true);
    }
  }, [codeError, nameError, building]);

  useEffect(() => {
    if (!fieldValues) return;
    setRoomMaintainenceInformation(
      normalizeSectionRows(fieldValues[RoomPropertyEnum.roomTypeMaintainence]),
    );
    setBuildingList(
      fillAutocompleteValues(
        fieldValues[RoomPropertyEnum.buildingCodes],
        DNDFieldsEnum.BUILDINGCODE,
        "",
      ),
    );
    setPostTypeOptions(
      removeSampleTestArray(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.postTypes],
          DNDFieldsEnum.POSTTYPECODE,
          DNDFieldsEnum.POSTTYPENAME,
        ) ?? [],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once when screen opens
  }, []);

  useEffect(() => {
    if (!editClicked || !postType || postTypeOptions.length === 0) return;
    const matched = matchPostTypeOption(postType, postTypeOptions);
    if (matched && matched !== postType) {
      setPostType(matched);
    }
  }, [postTypeOptions, editClicked, postType]);

  const roomTypeCodeHelperText = codeError
    ? !(roomTypeCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomTypeMaintainence.roomTypeCode
    : versaFieldHelperText.roomTypeMaintainence.roomTypeCode;

  const roomTypeNameHelperText = nameError
    ? !(roomTypeName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomTypeMaintainence.roomTypeName
    : versaFieldHelperText.roomTypeMaintainence.roomTypeName;

  const resortFeeHelperText = resortFeeError
    ? versaFieldErrors.roomTypeMaintainence.resortFee
    : versaFieldHelperText.roomTypeMaintainence.resortFee;

  return (
    <Stack direction="column" gap="10px">
      {/* Scroll target */}
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Room Type Information
              <Tooltip title={tooltipComponent(roomMaintainenceTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editRoomMaintainence(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRoomMaintainence(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Accordion
            expanded={openAccordion}
            onChange={() => setOpenAccordion(!openAccordion)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Room Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" width="100%" gap="20px">
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  width="100%"
                  gap="20px"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Room Type Code
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.roomTypeCode,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="roomtypeCode"
                      variant="outlined"
                      placeholder="Roomtype Code"
                      value={roomTypeCode ?? ""}
                      helperText={roomTypeCodeHelperText}
                      error={codeError}
                      sx={{ width: "100%" }}
                      onChange={(e: any) => {
                        setRoomTypeCode(e.target.value.toUpperCase());
                        setCodeError(false);
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Room Type Name
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.roomTypeName,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="roomtypeName"
                      variant="outlined"
                      placeholder="Roomtype Name"
                      helperText={roomTypeNameHelperText}
                      error={nameError}
                      value={roomTypeName ?? ""}
                      sx={{ width: "100%" }}
                      onChange={(e: any) => {
                        setRoomTypeName(e.target.value);
                        setNameError(false);
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  width="100%"
                  gap="20px"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Allowed Reservation Type
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.allowedReservationType,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Select
                      id="allowedReservationType"
                      value={allowedReservationType ?? ""}
                      onChange={(e: any) =>
                        setAllowedReservationType(e.target.value)
                      }
                      sx={{ width: "100%" }}
                    >
                      {typeList.map((route, index) => (
                        <MenuItem value={index} key={index}>
                          {route}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Buildings
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.building,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="buildingName"
                      multiple
                      disablePortal
                      options={removeSampleTestArray(buildingList)}
                      sx={{ width: "100%" }}
                      disabled={!buildingList}
                      value={building}
                      onChange={(_, newValue) => setBuilding(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buildings"
                          id="buildingName2"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  gap="20px"
                  width="100%"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      No of Rooms
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.roomCount,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="Noofrooms"
                      variant="outlined"
                      placeholder="No of rooms"
                      type="number"
                      value={roomCount ?? ""}
                      sx={{ width: "100%" }}
                      helperText={roomMaintainenceTooltip.roomCount}
                      onChange={(e: any) => {
                        setRoomCount(
                          e.target.value > 0 ? e.target.value : null,
                        );
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Maximum Guests
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.maxGuests,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="maxnoofguests"
                      variant="outlined"
                      placeholder="Maximum Guests"
                      type="number"
                      value={guestCount ?? ""}
                      sx={{ width: "100%" }}
                      helperText={roomMaintainenceTooltip.maxGuests}
                      onChange={(e: any) => {
                        setGuestCount(
                          e.target.value > 0 ? e.target.value : null,
                        );
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Maximum Nights
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.maxNights,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="maxnights"
                      variant="outlined"
                      placeholder="Maximum Nights"
                      type="number"
                      value={maxNights ?? ""}
                      sx={{ width: "100%" }}
                      helperText={roomMaintainenceTooltip.maxNights}
                      onChange={(e: any) => {
                        setMaxNights(
                          e.target.value > 0 ? e.target.value : null,
                        );
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  width="100%"
                  gap="20px"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      License From Date
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.licenseFromDate,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={licenseFromDate}
                        sx={{ width: "100%" }}
                        onChange={(value) => setLicenseFromDate(value)}
                      />
                    </LocalizationProvider>
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      License To Date
                      <Tooltip
                        title={tooltipComponent(
                          roomMaintainenceTooltip.licenseToDate,
                        )}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={licenseToDate}
                        sx={{ width: "100%" }}
                        onChange={(value) => {
                          if (
                            value &&
                            licenseFromDate &&
                            value.isBefore(licenseFromDate, "day")
                          ) {
                            setAlertMessage(
                              "License to date cannot be before license from date",
                            );
                            setAlertType("error");
                            setOpenAlert(true);
                            setLicenseToDate(null);
                          } else {
                            setLicenseToDate(value);
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Custom Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                className="form-checkbox-row form-two-col-row"
                direction="row"
                flexWrap="wrap"
                width="100%"
                gap="20px"
                border="1px solid lightgray"
                borderRadius={2}
                p={1}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      id="BeddedCheckbox"
                      checked={bedded}
                      onChange={(e: any) => setBedded(e.target.checked)}
                    />
                  }
                  label="Bedded?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isPermCheckbox"
                      checked={perm}
                      onChange={(e: any) => setPerm(e.target.checked)}
                    />
                  }
                  label="Is Perm?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="blockrequiredCheckbox"
                      checked={block}
                      onChange={(e: any) => setBlock(e.target.checked)}
                    />
                  }
                  label="Block Required?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="blockingremindercheckbox"
                      checked={blockingReminders}
                      onChange={(e: any) =>
                        setBlockingReminders(e.target.checked)
                      }
                    />
                  }
                  label="Blocking Reminder ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="allowoversellcheckbox"
                      checked={allowOversell}
                      onChange={(e: any) => setAllowOversell(e.target.checked)}
                    />
                  }
                  label="Allow Oversell ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="suiteroomcheckbox"
                      checked={suiteRoom}
                      onChange={(e: any) => setSuiteRoom(e.target.checked)}
                    />
                  }
                  label="Suite ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="passcoderoomunblockcheckbox"
                      checked={passcodeRoom}
                      onChange={(e: any) => setPasscodeRoom(e.target.checked)}
                    />
                  }
                  label="Passcode Room Unblock ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="ROHcheckbox"
                      checked={ROH}
                      onChange={(e: any) => setROH(e.target.checked)}
                    />
                  }
                  label="ROH ?"
                />
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      id="adultcheckbox"
                      checked={adult}
                      onChange={(e: any) => setAdult(e.target.checked)}
                    />
                  }
                  label="Adult ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="youthcheckbox"
                      checked={youth}
                      onChange={(e: any) => setYouth(e.target.checked)}
                    />
                  }
                  label="Youth ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="teencheckbox"
                      checked={teen}
                      onChange={(e: any) => setTeen(e.target.checked)}
                    />
                  }
                  label="Teen ?"
                /> */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      id="childcheckbox"
                      checked={child}
                      onChange={(e: any) => setChild(e.target.checked)}
                    />
                  }
                  label="Child ?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="todlerscheckbox"
                      checked={todlers}
                      onChange={(e: any) => setTodlers(e.target.checked)}
                    />
                  }
                  label="Todlers ?"
                /> */}
              </Stack>
              <Stack
                className="form-two-col-row"
                direction="row"
                width="100%"
                gap="20px"
                mt={3}
              >
                {/* <Stack direction="row" width="100%" gap="5px">
                  <TextField
                    id="villaHomeTemplateCode"
                    variant="outlined"
                    placeholder="Villa Home Template Code"
                    value={villaHomeTemplateCode ?? ""}
                    sx={{ width: "100%" }}
                    helperText={"Villa Home Template Code"}
                    onChange={(e: any) => {
                      setVillaHomeTemplateCode(e.target.value);
                    }}
                  />
                  <Tooltip title={tooltipComponent("")}>
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack> */}
                <Stack direction="column" gap="5px" width="100%">
                  <FormLabel component="legend">Villa Home Template</FormLabel>
                  <Select
                    id="villaHomeTemplateCode"
                    value={villaHomeTemplateCode ?? ""}
                    onChange={(e: any) =>
                      setVillaHomeTemplateCode(e.target.value)
                    }
                    sx={{ width: "100%" }}
                  >
                    {villaHomeTemplateList.map((templateCode, index) => {
                      return (
                        <MenuItem value={index} key={index}>
                          {templateCode}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Stack>
                <Stack direction="column" width="100%" gap="5px">
                  <FormLabel component="legend">Count Room As</FormLabel>
                  <TextField
                    id="countRoomAs"
                    variant="outlined"
                    placeholder="Count Room As"
                    type="number"
                    value={countRoomAs ?? ""}
                    sx={{ width: "100%" }}
                    helperText={"Count Room As"}
                    onChange={(e: any) => {
                      e.target.value > 0 && setCountRoomAs(e.target.value);
                    }}
                  />
                </Stack>
              </Stack>
              <Stack
                className="form-checkbox-row form-two-col-row"
                direction="row"
                width="100%"
                gap="20px"
                mt={3}
              >
                <Stack direction="column" width="100%">
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="petAllowedcheckbox"
                        checked={petAllowed}
                        onChange={(e: any) => setPetAllowed(e.target.checked)}
                      />
                    }
                    label="Pet Allowed?"
                  />
                  {petAllowed && (
                    <Stack direction="column" width="100%" gap="5px">
                      <FormLabel component="legend">No of Pets</FormLabel>
                      <TextField
                        id="noOfPets"
                        variant="outlined"
                        placeholder="No of Pets"
                        type="number"
                        value={petCount ?? ""}
                        sx={{ width: "100%" }}
                        helperText={""}
                        onChange={(e: any) => {
                          setPetCount(
                            e.target.value >= 0 ? e.target.value : null,
                          );
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
                <Stack direction="column" width="100%" gap="5px">
                  <Stack
                    className="form-checkbox-row form-two-col-row"
                    direction="row"
                    width="100%"
                    gap="20px"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="hourlycheckbox"
                          checked={isHourly}
                          onChange={(e: any) => {
                            setIsHourly(e.target.checked);
                            setIsNightly(!e.target.checked);
                          }}
                        />
                      }
                      label="Hourly?"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="nightlycheckbox"
                          checked={isNightly}
                          onChange={(e: any) => {
                            setIsNightly(e.target.checked);
                            setIsHourly(!e.target.checked);
                          }}
                        />
                      }
                      label="Nightly?"
                    />
                  </Stack>
                  {isNightly && (
                    <Stack
                      className="form-two-col-row"
                      direction="row"
                      gap="20px"
                      width="100%"
                    >
                      <Stack direction="column" width="100%" gap="5px">
                        <FormLabel component="legend">Gap Time</FormLabel>
                        <TextField
                          id="gapTime"
                          variant="outlined"
                          placeholder="Gap Time"
                          type="number"
                          value={gapTime ?? ""}
                          sx={{ width: "100%" }}
                          helperText={""}
                          onChange={(e: any) => {
                            setGapTime(
                              e.target.value >= 0 ? e.target.value : null,
                            );
                          }}
                        />
                      </Stack>
                      <Stack direction="column" width="100%" gap="5px">
                        <FormLabel component="legend">
                          Minimum Occupancy
                        </FormLabel>
                        <TextField
                          id="minimumOccupancy"
                          variant="outlined"
                          placeholder="Minimum Occupancy?"
                          type="number"
                          value={minOccupancy ?? ""}
                          sx={{ width: "100%" }}
                          helperText={""}
                          onChange={(e: any) => {
                            setMinOccupancy(
                              e.target.value > 0 ? e.target.value : null,
                            );
                          }}
                        />
                      </Stack>
                      <Stack direction="column" width="100%" gap="5px">
                        <FormLabel component="legend">
                          Maximum Occupancy
                        </FormLabel>
                        <TextField
                          id="maximumOccupancy"
                          variant="outlined"
                          placeholder="Maximum Occupancy?"
                          type="number"
                          value={maxOccupancy ?? ""}
                          sx={{ width: "100%" }}
                          helperText={""}
                          onChange={(e: any) => {
                            setMaxOccupancy(
                              e.target.value >= 0 ? e.target.value : null,
                            );
                          }}
                        />
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Resort Fee</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                className="form-two-col-row"
                direction="row"
                width="100%"
                gap="20px"
              >
                <Stack direction="column" width="100%" gap="5px">
                  <FormLabel component="legend">
                    Resort Fee Amount
                    <Tooltip
                      title={tooltipComponent(
                        roomMaintainenceTooltip.resortFee,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </FormLabel>
                  <TextField
                    id="ResortFeeAmount"
                    variant="outlined"
                    placeholder="Resort fee amount ?"
                    type="number"
                    helperText={resortFeeHelperText}
                    error={resortFeeError}
                    value={resortFee ?? ""}
                    sx={{ width: "100%" }}
                    onChange={(e: any) => {
                      e.target.value > 0 && setResortFee(e.target.value);
                      setResortFeeError(false);
                    }}
                  />
                </Stack>
                <Stack direction="column" gap="10px" width="100%">
                  <FormLabel component="legend">Resort Fee Type ?</FormLabel>
                  <Select
                    id="Resortfeetype"
                    value={resortFeeType ?? ""}
                    onChange={(e: any) => setResortFeeType(e.target.value)}
                  >
                    {resortFeeList.map((resortFee, index) => {
                      return (
                        <MenuItem value={index} key={index}>
                          {resortFee}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Stack>
                <Stack direction="column" gap="10px" width="100%">
                  <FormLabel component="legend">Frequency ?</FormLabel>
                  <Select
                    id="frequency"
                    value={frequency ?? ""}
                    onChange={(e: any) => setFrequency(e.target.value)}
                  >
                    {frequencyList.map((frequency, index) => {
                      return (
                        <MenuItem
                          value={frequency.toLowerCase().split(" ").join("_")}
                          key={index}
                        >
                          {frequency}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Stack>
                <Stack direction="column" gap="10px" width="100%">
                  <FormLabel component="legend">Post Type ?</FormLabel>
                  <Autocomplete
                    id="postType"
                    disablePortal
                    options={postTypeOptions}
                    sx={{ width: "100%" }}
                    disabled={postTypeOptions.length === 0}
                    value={postType ?? ""}
                    onChange={(_, newValue) => setPostType(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          postTypeOptions.length === 0
                            ? "Add post types on the Post Type screen first"
                            : "Post Type"
                        }
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Stack direction="row" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            {/* <Tooltip title={tooltipComponent("")}>
              <InfoIcon fontSize="small" />
            </Tooltip> */}
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updateRoomMaintainence : addRoomMaintainence
              }
              id="addNewRoomMaintenance"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Room
              Maintainence
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Room Maintainence Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {roomMaintainenceInformation.length > 0 ? (
                <DraggableItemList
                  itemData={roomMaintainenceInformation}
                  onOrderUpdate={(order) =>
                    setRoomMaintainenceInformation(order)
                  }
                  onEdit={(info) => {
                    editRoomMaintainence(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRoomMaintainence(id)}
                  code={DNDFieldsEnum.ROOMTYPECODE}
                  name={DNDFieldsEnum.ROOMTYPENAME}
                />
              ) : (
                <Typography variant="body2">
                  No Room Maintainence Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RoomtypeMaintainence;
